import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { PermissionService } from './permission.service';
import { CsrfService } from './csrf.service';
import { Permission } from '../enums/permission.enum';
import { UserProfile } from '../models/user.model';
import { clearAuthStorage, safeReadStorageString } from '../utils/auth-storage';
import { TranslateService } from '@ngx-translate/core';
import { DynamicDialogCleanupService } from './dynamic-dialog-cleanup.service';
import { ConfirmationService } from 'primeng/api';
import { InactivityLogoutService } from './inactivity-logout.service';
import { QueryClient } from '@tanstack/angular-query-experimental';

export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Login response from the backend.
 * NOTE: `token` and `refreshToken` are ALWAYS null after the cookie migration.
 *       The backend sets them as HttpOnly cookies and strips them from the body.
 *       These fields are kept only for type-compatibility during the migration window.
 */
export interface LoginResponse {
  /** Always null — token is delivered as an HttpOnly cookie. */
  token: string | null;
  /** Always null — refresh token is delivered as an HttpOnly cookie. */
  refreshToken: string | null;
  requiresTwoFactor: boolean;
  requiresSetup: boolean;
  challengeId: string | null;
  qrCodeImage: string | null;
  secret: string | null;
}

export interface EmailOtpChallengeResponse {
  challengeId: string;
  maskedEmail: string;
  expiresAt: string;
  sent: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  private loggedIn$ = new BehaviorSubject<boolean>(false);
  private authStateSubject = new BehaviorSubject<boolean>(this.isLoggedIn());
  authState$ = this.authStateSubject.asObservable();

  /**
   * In-memory auth state: true when the user has a valid session.
   * Derived only from a successful /authentication/me call.
   */



  constructor(
    private http: HttpClient,
    private router: Router,
    private permissionService: PermissionService,
    private csrfService: CsrfService,
    private injector: Injector,
  ) { }

  loadUserInfo(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${environment.apiUrl}/authentication/me`, { withCredentials: true }).pipe(
      tap((user) => {
        this.persistUserProfileHints(user);

        const backendPerms = Array.isArray(user.permissions) ? user.permissions : [];
        let bitmask = 0n;
        backendPerms.forEach((p: string) => {
          const key = p as keyof typeof Permission;
          if (Permission[key]) {
            bitmask |= BigInt(Permission[key]);
          }
        });

        // Super Admin receives all known frontend permissions.
        const isSuperAdmin = (user.roles || []).some((r: string) => {
          const stripped = r.replace(/^ROLE_/i, '');
          const normalized = stripped.toLowerCase().replace(/[_\s]+/g, '');
          return normalized === 'superadmin';
        });

        if (isSuperAdmin) {
          bitmask = Object.values(Permission).reduce(
            (allPermissions, permission) => allPermissions | permission,
            0n
          );

          bitmask &= ~Permission.CARD_VIEW_BASIC_EMPLOYEE_INFO;
          // BSA switches behaviour rather than widening access, so it is never implicit
          // (mirrors V193: Super Admin is not granted BSA automatically).
          bitmask &= ~Permission.BSA;
        }

        this.permissionService.updatePermissions(bitmask);
        localStorage.setItem('roles', JSON.stringify(Array.isArray(user.roles) ? user.roles : []));
        this.loggedIn$.next(true);
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Auth flows
  // ---------------------------------------------------------------------------

  login(email: string, password: string): Observable<LoginResponse> {
    // withCredentials is required so the browser stores the HttpOnly cookies
    // returned by the Set-Cookie response header.
    return this.http.post<LoginResponse>(
      `${environment.apiUrl}/authentication/login`,
     { email, password: password.trim() },
      { withCredentials: true }
    );
  }

  verifyMfa(code: string, challengeId: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${environment.apiUrl}/authentication/verify-mfa`,
      { code, challengeId },
      { withCredentials: true }
    );
  }

  enableMfa(code: string, challengeId: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${environment.apiUrl}/authentication/enable-mfa`,
      { code, challengeId },
      { withCredentials: true }
    );
  }

  sendEmailOtp(challengeId: string): Observable<EmailOtpChallengeResponse> {
    return this.http.post<EmailOtpChallengeResponse>(
      `${environment.apiUrl}/authentication/email-otp/send`,
      { challengeId },
      { withCredentials: true }
    );
  }

  verifyEmailOtp(code: string, challengeId: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${environment.apiUrl}/authentication/email-otp/verify`,
      { code: code.trim().toUpperCase(), challengeId },
      { withCredentials: true }
    );
  }

  /**
   * Triggers a silent token refresh.
   * The refresh token is sent automatically as an HttpOnly cookie — no JS involvement.
   * On success the backend sets a new access_token cookie (204 No Content body).
   */
  refreshAccessToken(): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/authentication/refresh`,
      null,
      { withCredentials: true }
    );
  }

  verifyToken(token: string): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/authentication/verify-token`, { params: { token } });
  }

  acceptInvitation(token: string, password: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/authentication/accept-invitation`, { token, password: password.trim() });
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/authentication/forgot-password`, { email });
  }

  resetPassword(userId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/authentication/reset-password/${userId}`, {});
  }

  resetMFA(userId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/authentication/reset-mfa/${userId}`, {});
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/authentication/change-password`,
     {
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim(),
      },
      { withCredentials: true }
     );
   
  }

  // ---------------------------------------------------------------------------
  // Session state
  // ---------------------------------------------------------------------------

  /**
   * Determines whether the current browser tab has an active in-memory session.
   * Startup re-hydration is handled by APP_INITIALIZER via loadUserInfo().
   */
  isLoggedIn(): boolean {
    return this.loggedIn$?.value;
  }

  // ---------------------------------------------------------------------------
  // Logout
  // ---------------------------------------------------------------------------

  logout() {
    // Ask the backend to clear the HttpOnly cookies server-side.
    this.http.post<void>(
      `${environment.apiUrl}/authentication/logout`,
      null,
      { withCredentials: true }
    ).subscribe({ error: () => { /* ignore network errors on logout */ } });

    this.clearSessionState();
  }

  /**
   * Clear only non-sensitive client-side state that does not contain the token.
   * The access/refresh tokens live in HttpOnly cookies — JS cannot touch them.
   */
  clearSessionState(redirectToAuth: boolean = true): void {
    this.stopInactivityWatcher();
    this.closeAllDialogs();
    clearAuthStorage();
    this.clearCachedBusinessData();
    this.loggedIn$.next(false);
    this.permissionService.clearPermissions();
    this.csrfService.clearToken();

    try {
      const translateService = this.injector.get(TranslateService);
      if (translateService) {
        translateService.use('en');
      }
    } catch (e) {
      console.warn('TranslateService could not be resolved:', e);
    }

    document.dir = 'ltr';

    const htmlTag = document.getElementsByTagName('html')[0];
    if (htmlTag) {
      htmlTag.setAttribute('lang', 'en');
    }

    if (redirectToAuth && this.router.url !== '/auth') {
      void this.router.navigateByUrl('/auth', { replaceUrl: true });
    }
  }

  /**
   * Removes finance and workflow state cached in localStorage.
   * These keys contain business data (process summaries, snapshots, workflow steps)
   * that should not persist across sessions on shared machines.
   */
  private clearCachedBusinessData(): void {
    const prefixes = [
      'em-fe:v3:process-service',
      'em-fe:v3:process-summary',
      'em-fe:workflow-steps',
    ];
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    // Server data cached in memory (employees directory…) belongs to the session that loaded it.
    this.injector.get(QueryClient, null)?.clear();
  }

  // ---------------------------------------------------------------------------
  // Non-sensitive profile helpers
  // ---------------------------------------------------------------------------

  getUserPractice(): string | null {
    return safeReadStorageString('userPractice');
  }

  getUserCategory(): string | null {
    return safeReadStorageString('userCategory');
  }

  getCurrentUserEmployeeNumber(): string | null {
    return safeReadStorageString('employeeNumber');
  }

  getCurrentUserEmployeeId(): number | null {
    const rawEmployeeId = safeReadStorageString('employeeId');
    if (rawEmployeeId === null) {
      return null;
    }

    const employeeId = Number(rawEmployeeId);
    return Number.isFinite(employeeId) ? employeeId : null;
  }

  /**
   * Returns the current user's numeric primary key (app_user.id).
   * Used as a fallback identity check when no employee record is linked.
   */
  getCurrentUserId(): number | null {
    const raw = safeReadStorageString('userId');
    if (raw === null) {
      return null;
    }

    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  }

  private persistUserProfileHints(user: UserProfile): void {
    this.writeOptionalAuthString('userPractice', user.practice);
    this.writeOptionalAuthString('userCategory', user.categoryShortLabel);
    this.writeOptionalAuthString('employeeNumber', user.employeeNumber);
    this.writeOptionalAuthNumber('employeeId', user.employeeId);
    this.writeOptionalAuthNumber('userId', user.id);
  }

  private writeOptionalAuthString(
    key: 'employeeNumber' | 'userPractice' | 'userCategory',
    value: string | null | undefined
  ): void {
    const normalizedValue = this.readOptionalProfileString(value);
    if (normalizedValue !== null) {
      localStorage.setItem(key, normalizedValue);
      return;
    }

    localStorage.removeItem(key);
  }

  private writeOptionalAuthNumber(key: 'employeeId' | 'userId', value: number | null | undefined): void {
    if (typeof value === 'number' && Number.isFinite(value)) {
      localStorage.setItem(key, String(value));
      return;
    }

    localStorage.removeItem(key);
  }

  private readOptionalProfileString(value: string | null | undefined): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  }

  private stopInactivityWatcher(): void {
    try {
      this.injector.get(InactivityLogoutService).stopWatching();
    } catch {
      // Ignore if the watcher has not been created yet.
    }
  }

  /**
   * Close all open DynamicDialogs and ConfirmDialogs so they don't
   * linger on top of the login page after session expiry.
   */
  private closeAllDialogs(): void {
    try {
      this.injector.get(DynamicDialogCleanupService).closeAll();
    } catch {
      // Ignore if the cleanup service has not been created yet.
    }
    try {
      this.injector.get(ConfirmationService).close();
    } catch {
      // Ignore if the confirmation service has not been created yet.
    }
  }

  getStoredRoles(): string[] {
    const rolesStr = localStorage.getItem('roles');
    return rolesStr ? JSON.parse(rolesStr) : [];
  }

  hasRole(role: string): boolean {
    return this.getStoredRoles().includes(role);
  }

  isSuperAdmin(): boolean {
    return this.getStoredRoles().some((role) => {
      const normalized = role.replace(/^ROLE_/i, '').toLowerCase().replace(/[_\s]+/g, '');
      return normalized === 'superadmin';
    });
  }
}
